import { Component, OnInit, OnDestroy, HostListener, ElementRef } from "@angular/core";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { ConfirmationService, MessageService } from "primeng/api";
import { UserService } from "../service/uesr.service";
import { NavigationEnd, Router } from "@angular/router";
import { AuthService } from "../../auth/auth.service";
import { AdminAccessService } from "../../services/admin-access.service";

const RAIL_KEY = "isso.adminSidebarRail";

/** Last path segment to the heading shown in the top bar. */
const TITLES: { [path: string]: string } = {
  dashboard: "Dashboard",
  "admin-school": "Schools",
  student: "Students",
  game: "Games",
  subgame: "Sub Games",
  "map-game": "Map Game",
  "coach-data": "Coach Entries",
  event: "Events",
  upcomingevent: "Upcoming Events",
  manageevent: "Active / Inactive Event",
  manageeventreport: "Release Certificates",
  ManageTeamReport: "Manage Team Entries",
  attendance: "Attendance",
  "qr-attendance": "QR Attendance",
  merit: "Add Merit",
  "print-merit": "Print Merit",
  "consolited-merit": "Consolidated Merit",
  report: "Report",
  certificate: "Certificate",
  "coach-certificate": "Coach Certificate",
  "consolited-report": "Consolidated Report",
  "volunteer-certificate": "Volunteer Certificate",
  "team-event-report": "Team Event Report",
  "id-card": "Download ID Card",
  payment: "Payment Summary",
  "payment-invoice": "Payment",
  "download-invoice": "Invoice",
  "download-receipt": "Receipt",
  "admin-payment": "Online Fee",
  payments: "Payments",
  "sgfi-settings": "SGFI Settings",
  "isf-fee": "ISF School Fee",
  "payment-settings": "Payment Settings",
  affiliation: "Affiliation Fees",
  "sgfi-entries": "SGFI Entries",
  "sgfi-events": "SGFI Events",
  "sgfi-download-payment": "SGFI - Download Payment",
  "profile-view-student": "View Student",
  "search-student-profile": "Search Student",
  "manual-payment": "Manual Payment",
  "admin-student-enrollment": "Student Enrollment",
  transfers: "Transfer Requests",
  content: "Dashboard Content",
  news: "News & Announcements",
  "medal-tally": "Medal Tally",
  "site-notice": "Site Notice",
  "mail-settings": "Mail Settings",
  playzone: "PlayZone Quizzes",
  "media-vault": "Media Vault",
  subscribers: "Subscribers",
  webData: "Web Calender",
  "isf-data": "ISF Data",
  "user-mgt": "Admin Users & Roles",
  "change-pass": "Change Password",
  "no-access": "No access",
};

@Component({
  selector: "app-admin",
  templateUrl: "./admin.component.html",
  styleUrls: ["./admin.component.css"],
  providers: [MessageService, ConfirmationService],
})
export class AdminComponent implements OnDestroy, OnInit {
  private destroy$ = new Subject<void>();
  adminMenuData: Object;

  /** Off-canvas drawer, phones only. */
  menuOpen = false;
  /** Icon-only rail. Remembered between visits. */
  collapsed = false;
  userMenuOpen = false;
  pageTitle = "";
  // Defaulting isSuper to true made a failed access load look like a working
  // super admin with an empty menu. Until the server answers we claim nothing.
  me: { name: string; email: string; isSuper: boolean } = { name: "", email: "", isSuper: false };
  accessLoaded = false;
  accessFailed = false;

  constructor(
    private confirmation: ConfirmationService,
    private messageService: MessageService,
    private router: Router,
    private userSerview: UserService,
    private authService: AuthService,
    private access: AdminAccessService,
    private host: ElementRef<HTMLElement>
  ) {}

  ngOnInit() {
    this.adminMenuData = "";
    this.collapsed = this.readRail();
    this.adminMenu();
    // What this administrator may open can change while they are signed in.
    this.loadAccess();

    this.setTitle(this.router.url);
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe((e: NavigationEnd) => {
        this.setTitle(e.urlAfterRedirects || e.url);
        this.closeUserMenu();
        if (this.isNarrow()) this.closeMenu();
      });
  }

  logout() { this.authService.logout(); }

  /**
   * What this administrator may open.
   *
   * A failure here is not "no access" - it is "access unknown". The nav hides
   * everything either way, so the screen has to say which it is, or an outage
   * reads as a permissions problem.
   */
  loadAccess() {
    this.accessFailed = false;
    this.access.refresh(
      who => { this.me = who; this.accessLoaded = true; },
      () => { this.accessFailed = true; },
    );
  }

  /** The mark in the bar leads wherever this administrator is allowed to start. */
  get homeRoute(): string { return this.access.homeRoute(); }

  /** Nav visibility. Mirrors the route guard, so no link leads to a refusal. */
  can(key: string): boolean { return this.access.can(key); }
  canAny(keys: string[]): boolean { return this.access.canAny(keys); }

  get initials(): string {
    const source = (this.me.name || this.me.email || "A").trim();
    const parts = source.split(/[\s@._-]+/).filter(p => !!p);
    const letters = parts.slice(0, 2).map(p => p.charAt(0)).join("");
    return letters.toUpperCase() || "A";
  }

  /**
   * Menu LABELS only.
   *
   * The routes used to come from this response too, as
   * `routerLink="{{ adminMenuData[n]?.routing }}"`. That binding is an empty
   * string until the request lands, and an empty routerLink resolves to the
   * current URL - so routerLinkActive marked every one of those rows as the
   * open page. It only recomputes on a navigation, and the data arrives after
   * the first one, so the whole menu stayed lit. The routes are fixed and now
   * live in the template; the names still come from the database, so renaming
   * a menu row still renames the item.
   */
  adminMenu() {
    this.userSerview.getMenuData("issoadmin").pipe(takeUntil(this.destroy$)).subscribe(response => {
      if (response !== "") this.adminMenuData = response;
    });
  }

  /* ── sidebar ──────────────────────────────────────────────────────────── */

  /** Hamburger - the drawer, on a phone. */
  toggleMenu() {
    this.menuOpen = !this.menuOpen;
    if (!this.menuOpen) this.closeDropdown();
  }

  /**
   * Rail mode. Collapsed the sidebar keeps the icons and drops the labels; a
   * group clicked in that state flies its items out to the side, so nothing
   * becomes unreachable.
   */
  toggleCollapsed() {
    this.collapsed = !this.collapsed;
    this.closeDropdown();
    try { localStorage.setItem(RAIL_KEY, this.collapsed ? "1" : "0"); } catch {}
  }

  private readRail(): boolean {
    try { return localStorage.getItem(RAIL_KEY) === "1"; } catch { return false; }
  }

  /**
   * Open one group, close any other.
   *
   * Resolved from the clicked element rather than by id: there are eleven of
   * these across ten menus, and giving each its own flag would mean eleven
   * properties to keep in step. A nested group closes its own siblings only,
   * so its parent stays open.
   */
  toggleDropdown(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    const li = (event.currentTarget as HTMLElement).closest("li") as HTMLElement | null;
    if (!li) return;
    const wasOpen = li.classList.contains("is-open");
    const scope: HTMLElement = li.parentElement || this.host.nativeElement;
    const siblings = scope.children;
    for (let i = 0; i < siblings.length; i++) siblings[i].classList.remove("is-open");
    if (!wasOpen) li.classList.add("is-open");
  }

  private closeDropdown() {
    const open = this.host.nativeElement.querySelectorAll("li.is-open");
    for (let i = 0; i < open.length; i++) open[i].classList.remove("is-open");
  }

  /**
   * Picking a destination closes the drawer on a phone, where it covers the
   * page. On a desktop the sidebar stays as it is - collapsing the group you
   * just navigated from loses your place.
   */
  closeMenu() {
    this.menuOpen = false;
    if (this.isNarrow() || this.collapsed) this.closeDropdown();
  }

  toggleUserMenu(event: Event) {
    event.stopPropagation();
    this.userMenuOpen = !this.userMenuOpen;
  }
  closeUserMenu() { this.userMenuOpen = false; }

  private isNarrow(): boolean {
    return typeof window !== "undefined" && window.innerWidth < 992;
  }

  private setTitle(url: string) {
    const path = (url || "").split("?")[0].split("/").filter(p => !!p).pop() || "";
    this.pageTitle = TITLES[path] || "";
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Node;
    this.closeUserMenu();
    const side = this.host.nativeElement.querySelector(".side");
    if (side && side.contains(target)) return;
    // The drawer sits over the page, so a tap outside it means "close".
    if (this.menuOpen) { this.menuOpen = false; this.closeDropdown(); }
    // A flyout from the rail is the same: it floats over the content.
    else if (this.collapsed) this.closeDropdown();
  }

  @HostListener("document:keydown.escape")
  onEscape() {
    this.closeUserMenu();
    this.menuOpen = false;
    this.closeDropdown();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
